import type {
  ConnectionEventsResponse,
  ConnectionsResponse,
  ConnectionsScope,
  SettingsConnection,
} from '@skupreme/api-contracts/core';
import { BaseService } from '@utility/services/base';

/**
 * GET settings/rebuild (EN-4203), read only. `v2` is true when the account's
 * settings are served by the new system; the Rebuild settings tab then shows
 * what it holds. The server picks the account from the session, never from
 * the request. A section named in `unavailable` could not be read here yet,
 * and its field is absent.
 */
export type RebuildSettingsResponse = { v2: false } | RebuildV2Settings;

export type RebuildSection = 'qtyRules' | 'routingRules' | 'carriers';

export type RebuildV2Settings = {
  v2: true;
  qtyRules?: RebuildQtyRules;
  routing: RebuildRouting;
  carriers?: RebuildCarrier[];
  notYet: string[];
  unavailable?: RebuildSection[];
};

/** A connection; `name` is null when the new system cannot name it yet. */
export type RebuildConnection = { connectionId: string; name: string | null };

export type RebuildQtyRule = RebuildConnection & {
  /** null: no connection rule (no cap, holdback 0). */
  rule: {
    holdbackQty: number;
    publishableQty: number | null;
    allocatePrimary: boolean;
  } | null;
  itemRuleCount: number;
};

export type RebuildQtyRules = {
  connections: RebuildQtyRule[];
  allMarketplacesItemRuleCount: number;
};

export type RebuildRoutingRule = {
  id: string;
  priority: number;
  conditions: unknown;
  target: RebuildConnection;
  targetNodeId: string | null;
  requireStock: boolean;
};

export type RebuildRouting = {
  automatic: boolean;
  defaultProvider: RebuildConnection | null;
  rules?: RebuildRoutingRule[];
};

export type RebuildCarrier = {
  carrier: string;
  kind: 'own' | 'third_party';
  name: string;
  isDefault: boolean;
  locations: string[];
};

/* The connection list's shapes are the API contract's (@skupreme/api-contracts settings/connections). */
export type {
  ConnectionEventsResponse,
  ConnectionHealthEvent,
  ConnectionsResponse,
  ConnectionsScope,
  PausedStream,
  SettingsConnection,
} from '@skupreme/api-contracts/core';

export type RebuildConnectionKind = SettingsConnection['kind'];

const rebuildSettingsPath = 'settings/rebuild';
const connectionsPath = 'settings/connections';

class RebuildSettings {
  get = (): Promise<RebuildSettingsResponse> =>
    BaseService.get(rebuildSettingsPath);

  connections = (scope: ConnectionsScope): Promise<ConnectionsResponse> =>
    BaseService.get(
      scope === 'account'
        ? `${connectionsPath}?scope=account`
        : connectionsPath,
    );

  connectionEvents = (
    connectionId: string,
  ): Promise<ConnectionEventsResponse> =>
    BaseService.get(
      `${connectionsPath}/${encodeURIComponent(connectionId)}/events`,
    );
}

export default new RebuildSettings();
